import {ChangeEvent, useState} from 'react';
import isEqual from 'lodash/isEqual';
import map from 'lodash/map';
import sortBy from 'lodash/sortBy';

import {
    AgeCategory,
    AudienceCategories,
    AudienceInterestInfo,
    AudienceInterestValue,
    AudienceRequest,
    AudienceRequestUpdate,
    AudienceSegmentInfo,
    AudienceSegmentValue,
    AudienceSettingsType,
    AudienceType,
    AudienceUpdateParams,
    CitySizeCategory,
    ExtendedAudienceCategoryId,
    ExtendedCategoryValues,
    GenderCategory,
    GeoCategory,
    GeoCountry,
    GeoValue,
    isAgeCategory,
    Quota,
    SurveysGoal
} from 'client/common/types';

import {AudienceTabChoice, TargetingsProps} from 'client/common/components/audience-settings/types';

import {
    getAudienceTypeOptions,
    getDescription
} from 'client/common/components/audience-settings/helpers';

import {
    AudienceSegments,
    AudienceSizeDO,
    AudienceTargeting,
    TargetingInterests,
    TargetingOptions
} from 'client/common/utils/audience';
import {
    DEFAULT_GENDER_QUOTAS,
    genRequestQuotas
} from 'client/common/components/audience-settings/quotas/utils';
import {getIsCreativeBox} from 'client/common/utils/box';

import {UserRoles, useUserRoles} from 'client/common/hooks/user';
import {useBoxType} from 'client/common/hooks/box';
import {useSendGoalOnce} from 'client/common/hooks/metrika';
import {useSurveyFeatures} from 'client/common/hooks/features';
import {useAudienceRequest, useAudienceSegments} from 'client/common/hooks/audience';

import {useSurveySlug} from 'client/common/contexts/slug';

// eslint-disable-next-line complexity, max-statements
function getAudienceUpdateParams(
    audienceUpdateParams: AudienceUpdateParams,
    data: {
        selectedCountry: GeoCountry;
        boxType: TargetingsProps['boxType'];
        request: AudienceRequest;
        segments: AudienceSegmentInfo[];
        interests: AudienceInterestInfo[];
        isWithGeoType: boolean;
        userRoles: UserRoles;
    }
): AudienceRequestUpdate {
    const {type, country, categories, description} = audienceUpdateParams;

    const {selectedCountry, boxType, userRoles, request, interests, segments, isWithGeoType} = data;

    const selectedSegments = request.segments;
    const defaultSegments = AudienceSegments.getDefaultSegments(segments);

    const defaultInterest = TargetingInterests.getInterestOptions(interests)[0].value;
    const [interest] = request.interests || [];

    const countryGeoCategory: AudienceCategories = {
        categoryId: 'geo',
        value: [country || selectedCountry || GeoValue.Russia]
    };

    const params: AudienceRequestUpdate = {
        categories: categories ?? [countryGeoCategory],
        interests: null,
        quotas: null,
        type: request.type
    };

    if (type) {
        params.type = type === 'GEO' ? AudienceType.RespParams : type;

        params.size = new AudienceSizeDO({
            categories: params.categories || [],
            targetingCountry: selectedCountry,
            boxType,
            type: params.type,
            size: request.size,
            userRoles
        }).getValidSize();
    }

    if (country) {
        params.size = new AudienceSizeDO({
            categories: params.categories || [],
            targetingCountry: country,
            boxType,
            type: request.type,
            size: request.size,
            userRoles
        }).getValidSize();
    }

    if (type === AudienceType.MediaTargeting) {
        params.interests = interest ? [interest] : [defaultInterest];
    } else if (type === AudienceType.YaAudience) {
        params.segments = selectedSegments.length > 0 ? selectedSegments : defaultSegments;
    } else if (type === AudienceType.RespParams && isWithGeoType) {
        // это нужна для креативов, из-за того как соц.дем как разделен в креативах
        params.categories = [
            AudienceTargeting.WIDE_AGE_CATEGORY,
            AudienceTargeting.WIDE_GENDER_CATEGORY,
            countryGeoCategory
        ];
    } else if (type === AudienceType.RespParams) {
        // отфильтровываем расширенные категории ОМИ
        params.categories = (params.categories ?? [countryGeoCategory]).filter(
            ({categoryId}) =>
                !AudienceTargeting.EXTENDED_CATEGORY_VALUES.includes(categoryId as any)
        );
    } else if (type === AudienceType.Quotas) {
        params.quotas = genRequestQuotas(DEFAULT_GENDER_QUOTAS);
    }

    if (description) {
        params.description = description;
    }

    return params;
}

// eslint-disable-next-line max-statements
export function useAudienceTargeting(
    props: Pick<TargetingsProps, 'boxType' | 'request' | 'categories' | 'interests' | 'onChange'>
) {
    const {boxType, request, categories, interests, onChange} = props;

    const sendGoalOnce = useSendGoalOnce();
    const segmentsData = useAudienceSegments();
    const segments = segmentsData.data ?? [];

    const targetingOptions = TargetingOptions.getTargetingOptions({
        categories,
        boxType,
        country: undefined
    });
    const countriesOptions = TargetingOptions.getCountriesOptions(categories, boxType);

    const initialSelectedCountry = AudienceTargeting.getSelectedCountry({
        request,
        boxType,
        categories
    });

    const [selectedCountry, setSelectedCountry] = useState(initialSelectedCountry);

    const slug = useSurveySlug();
    const features = useSurveyFeatures(slug);
    const userRoles = useUserRoles();

    const audienceTypeOptions = getAudienceTypeOptions({
        boxType,
        request,
        segments,
        userRoles,
        features: features?.data ?? {},
        selectedCountry
    });

    const isWithGeoType = audienceTypeOptions.some(({value}) => value === 'GEO');

    const selectedAudienceType = TargetingOptions.getActiveAudienceType(request, isWithGeoType);

    const description = getDescription(request);

    const availableCategories = TargetingOptions.getAvailableCategories(
        boxType,
        selectedAudienceType,
        request.categories
    );

    const [interest] = request.interests || [];

    const defaultInterest = TargetingInterests.getInterestOptions(interests)[0].value;
    const defaultSegments = AudienceSegments.getDefaultSegments(segments);
    const defaultGender = map(targetingOptions.gender, 'value');

    const selectedSegments = request.segments;

    const audienceUpdateParamsData = {
        boxType,
        interests,
        userRoles,
        isWithGeoType,
        request,
        segments,
        selectedCountry
    };

    const handleTypeChange = async (type: string) => {
        sendGoalOnce(SurveysGoal.SelectAudience);

        await onChange(
            getAudienceUpdateParams(
                {
                    type: type as AudienceSettingsType
                },
                audienceUpdateParamsData
            )
        );
    };

    const handleTypeChangeEvent = async (event: ChangeEvent<HTMLSelectElement>) => {
        return handleTypeChange(event.target.value);
    };

    const handleDescriptionChange = async (event: ChangeEvent<HTMLTextAreaElement>) => {
        await onChange(
            getAudienceUpdateParams(
                {
                    description: event.target.value
                },
                audienceUpdateParamsData
            )
        );
    };

    const handleInterestChange = async (value: AudienceInterestValue) => {
        sendGoalOnce(SurveysGoal.SelectAudience);
        await onChange({
            interests: [value],
            quotas: null,
            type: request.type
        });
    };

    const handleSegmentsChange = async (value: AudienceSegmentValue[]) => {
        sendGoalOnce(SurveysGoal.SelectAudience);
        await onChange({
            segments: value,
            interests: null,
            quotas: null,
            type: request.type
        });
    };

    const handleQuotasChange = async (quotas: Quota[]) => {
        sendGoalOnce(SurveysGoal.SelectAudience);
        const quotasWithGeo = quotas.map(quota => {
            const geoCategory = quota.categories.find(({categoryId}) => categoryId === 'geo');

            if (!geoCategory) {
                quota.categories.push({
                    categoryId: 'geo',
                    value: [selectedCountry]
                });
            }

            return quota;
        });

        await onChange({
            quotas: quotasWithGeo,
            interests: null,
            type: request.type
        });
    };

    const handleCountryChange = async (country: string) => {
        const newCountry = country as GeoCountry;
        const modified = request.categories.filter(({categoryId}) => categoryId !== 'geo');

        modified.push({
            categoryId: 'geo',
            value: [newCountry]
        });

        setSelectedCountry(newCountry);

        const changes: AudienceUpdateParams = {
            country: newCountry,
            type: request.type as AudienceSettingsType
        };

        // В про версии даем запускаться только на всю страну, если не Россия
        if (
            newCountry !== GeoValue.Russia &&
            (!boxType || boxType === 'ux') &&
            request.type !== AudienceType.RespParams
        ) {
            changes.type = AudienceType.RespParams;
        }

        if (newCountry === GeoValue.Russia) {
            changes.categories = modified;
        }

        sendGoalOnce(SurveysGoal.SelectAudience);
        await onChange(getAudienceUpdateParams(changes, audienceUpdateParamsData));
    };

    const handleCountryChangeEvent = async (event: ChangeEvent<HTMLSelectElement>) => {
        return handleCountryChange(event.target.value);
    };

    const handleGeoChange = async (value: GeoCategory['value']) => {
        const restCategories = request.categories.filter(({categoryId}) => categoryId !== 'geo');

        await onChange({
            categories: [...restCategories, {categoryId: 'geo', value}],
            interests: request.interests?.length ? request.interests : null,
            quotas: null,
            type: request.type
        });
    };

    const handleCitySizeChange = async (value: CitySizeCategory['value']) => {
        const restCategories = request.categories.filter(
            ({categoryId}) => categoryId !== 'city_size'
        );

        await onChange({
            categories: value.length
                ? [...restCategories, {categoryId: 'city_size', value}]
                : restCategories,
            interests: null,
            quotas: null,
            type: request.type
        });
    };

    const handleGenderChange = async (value: GenderCategory['value']) => {
        sendGoalOnce(SurveysGoal.SelectAudience);

        const modified = request.categories.filter(({categoryId}) => categoryId !== 'gender');

        if (!isEqual(sortBy(value), sortBy(defaultGender))) {
            const category: GenderCategory = {
                categoryId: 'gender',
                // Из мультиселекта передаётся список, но API нужна строка
                value: value[0] as '0' | '1'
            };

            modified.push(category);
        }

        await onChange({
            categories: modified,
            interests: null,
            quotas: null,
            type: request.type
        });
    };

    const handleAgeChange = async (value: AgeCategory['value']) => {
        sendGoalOnce(SurveysGoal.SelectAudience);

        const modified = request.categories.filter(i => !isAgeCategory(i));

        const ageCategory: AgeCategory = {
            categoryId: 'age',
            value: sortBy(value)
        };

        modified.push(ageCategory);

        await onChange({
            categories: modified,
            interests: null,
            quotas: null,
            type: request.type
        });
    };

    const handleExtendedTargetingChange = <T extends keyof typeof targetingOptions>(key: T) => {
        const defaultValue = map(targetingOptions[key], 'value');

        return async (value: ExtendedCategoryValues) => {
            sendGoalOnce(SurveysGoal.SelectAudience);

            const modified = request.categories.filter(
                // Даем выбрать только один из "узких" таргетингов ОМИ
                ({categoryId}) =>
                    !AudienceTargeting.EXTENDED_CATEGORY_VALUES.includes(
                        categoryId as ExtendedAudienceCategoryId
                    )
            );

            if (!isEqual(sortBy(value), sortBy(defaultValue))) {
                // Из мультиселекта передаётся список, но API нужна строка
                modified.push({
                    categoryId: key,
                    // @ts-ignore
                    value: value[0]
                });
            }

            await onChange({
                categories: modified,
                interests: null,
                quotas: null,
                type: request.type
            });
        };
    };

    return {
        handleAgeChange,
        handleDescriptionChange,
        handleSegmentsChange,
        handleInterestChange,
        handleGenderChange,
        handleGeoChange,
        handleTypeChange,
        handleCountryChangeEvent,
        handleQuotasChange,
        handleCountryChange,
        handleTypeChangeEvent,

        handleCitySizeChange,
        handleHaveCarChange: handleExtendedTargetingChange('have_car'),
        handleHaveChildrenChange: handleExtendedTargetingChange('have_children'),
        handleFamilyStatusChange: handleExtendedTargetingChange('family_status'),
        handleCohabitantsAmountChange: handleExtendedTargetingChange('cohabitants_amount'),

        description,
        userRoles,
        countriesOptions,
        audienceTypeOptions,
        selectedAudienceType,
        defaultInterest,
        interest,
        selectedSegments,
        defaultSegments,
        selectedCountry,
        availableCategories
    };
}

export function useInitialAudienceTab() {
    const slug = useSurveySlug();
    const request = useAudienceRequest(slug);
    const boxType = useBoxType(slug);
    const isCreativeBox = getIsCreativeBox(boxType);

    if (!request.data) {
        return AudienceTabChoice.Standard;
    }

    if (!isCreativeBox && AudienceTargeting.isDefaultAudience(request.data)) {
        return AudienceTabChoice.Default;
    }

    if (AudienceTargeting.isExtendedAudience(request.data)) {
        return AudienceTabChoice.Extended;
    }

    return AudienceTabChoice.Standard;
}
